import { matchPath } from 'react-router';

/**
 * Every path of the old app and its new home, as data. `:symbol` in `from` is carried over to `to`, encoded again.
 * Old bookmarks and home-screen shortcuts keep working; the redirect replaces the history entry.
 *
 * `/symbols` collides with the new `/symbols/:market` only at that exact path, which is all `from` matches.
 */
export const legacyRedirects = [
  { from: '/group-trade', to: '/groups/spot' },
  { from: '/future-group-trade', to: '/groups/usdm' },
  { from: '/new-group-trade', to: '/groups/spot/new' },
  { from: '/new-future-group-trade', to: '/groups/usdm/new' },
  { from: '/symbols', to: '/symbols/spot' },
  { from: '/symbol-groups', to: '/symbols/spot?tab=groups' },
  { from: '/future-symbols', to: '/symbols/usdm' },
  { from: '/future-symbol-groups', to: '/symbols/usdm?tab=groups' },
  { from: '/orders', to: '/bot/spot/orders' },
  { from: '/futures-orders', to: '/bot/usdm/orders' },
  { from: '/order-details/:symbol', to: '/bot/spot/orders/:symbol' },
  { from: '/futures-order-details/:symbol', to: '/bot/usdm/orders/:symbol' },
  { from: '/trades', to: '/bot/spot/history' },
  { from: '/futures-trades', to: '/bot/usdm/history' },
  { from: '/klines-chart', to: '/charts/klines' },
  { from: '/top-line-chart', to: '/charts/lines?side=top' },
  { from: '/bottom-line-chart', to: '/charts/lines?side=bottom' },
  { from: '/line-trade', to: '/line-trades' },
  // Line retest was dropped: its old address leads home.
  { from: '/line-retest', to: '/' },
] as const satisfies readonly { from: string; to: string }[];

function decodeSafely(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** Fills `:name` segments of a target with the matched values, each encoded once. */
function fill(template: string, params: Record<string, string | undefined>): string {
  return template.replace(/:([A-Za-z]\w*)/g, (_whole, name: string) => encodeURIComponent(decodeSafely(params[name] ?? '')));
}

/**
 * The new address for an old path, or null when the path is not an old one. `pathname` is the raw (encoded) path.
 * The old query string is kept; the target's own parameters win.
 */
export function legacyTarget(pathname: string, search = ''): string | null {
  for (const { from, to } of legacyRedirects) {
    const match = matchPath({ path: from, end: true }, pathname);
    if (!match) continue;

    const [targetPath = '/', targetQuery = ''] = fill(to, match.params).split('?');
    const query = new URLSearchParams(search);
    for (const [key, value] of new URLSearchParams(targetQuery)) query.set(key, value);

    const queryString = query.toString();
    return queryString ? `${targetPath}?${queryString}` : targetPath;
  }

  return null;
}
